import * as React from 'react'
import { observer } from 'mobx-react'
import { Sandwich, SandwichBun, SandwichContent } from 'components/Sandwich'
import ScrollView from 'components/ScrollView'
import Avatar from 'components/Avatar'
import QuestionIcon from '../../../../components/QuestionIcon'
import CompletionToggle from 'components/CompletionToggle'
import Text from 'components/Text'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { isNotNil } from 'utils/object-util'
import {
  SplitViewHeader,
  SplitViewHeaderTitle,
  SplitViewSidebarListItem,
  SplitViewSidebarListItemArrow,
  SplitViewSidebarListItemSubtitle,
  SplitViewSidebarListItemTitle,
} from 'components/SplitView'
import { OutlineViewModel, StepContainer } from '../../../OutlineViewModel'

interface OutlineProps {
  vm: OutlineViewModel
  title: string
}

/**
 * The Outline
 */
export default observer(function Outline({ vm, title }: OutlineProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  let foundIncompleteStep = false
  return (
    <Sandwich stretch>
      <SandwichBun>
        <SplitViewHeader compact scrollOffset={scrollOffset}>
          <SplitViewHeaderTitle data-test="draft-title" title={title}>
            {title}
          </SplitViewHeaderTitle>
        </SplitViewHeader>
      </SandwichBun>
      <SandwichContent>
        <ScrollView ref={scrollRef}>
          {vm.stepsContainers.map((stepContainer) => {
            const pulse = !foundIncompleteStep && !stepContainer.step.valid
            if (!foundIncompleteStep && pulse) {
              foundIncompleteStep = true
            }
            return (
              <OutlineItem
                key={stepContainer.step.stepId}
                stepContainer={stepContainer}
                pulse={pulse}
              />
            )
          })}
        </ScrollView>
      </SandwichContent>
    </Sandwich>
  )
})

interface OutlineItemProps {
  stepContainer: StepContainer
  pulse: boolean
}

const VISUAL_SIZE = 32

/**
 " Outline Item
 */
export const OutlineItem = observer(function OutlineItem({
  pulse,
  stepContainer: { step },
}: OutlineItemProps) {
  const stepVisual =
    step.outlineDisplay.visual &&
    (step.outlineDisplay.visual.type === 'Image' ? (
      <Avatar
        size={VISUAL_SIZE}
        subject={{ avatar: step.outlineDisplay.visual.url }}
      />
    ) : step.outlineDisplay.visual.type === 'QuestionIcon' ? (
      <QuestionIcon
        size={VISUAL_SIZE}
        type={step.outlineDisplay.visual.questionType}
      />
    ) : (
      <Avatar size={VISUAL_SIZE} subject={null} />
    ))

  return (
    <SplitViewSidebarListItem
      active={step.isActive}
      data-test={`outline-item-${step.stepId}`}
      aria-current={step.isActive ? 'step' : undefined}
      extras={<SplitViewSidebarListItemArrow />}
      onClick={step.becomeActive}
      visual={
        <CompletionToggle
          size={VISUAL_SIZE}
          completed={isNotNil(stepVisual)}
          readOnly
          pulse={pulse}
        >
          {stepVisual}
        </CompletionToggle>
      }
    >
      <SplitViewSidebarListItemTitle
        wrap
        title={step.outlineDisplay.title}
        data-test={`outline-item-title-${step.stepId}`}
      >
        {step.outlineDisplay.title}
      </SplitViewSidebarListItemTitle>
      {step.outlineDisplay.value && (
        <SplitViewSidebarListItemSubtitle
          wrap
          title={step.outlineDisplay.value}
          data-test={`outline-item-value-${step.stepId}`}
        >
          <Text
            text={step.outlineDisplay.shortValue || step.outlineDisplay.value}
          />
        </SplitViewSidebarListItemSubtitle>
      )}
    </SplitViewSidebarListItem>
  )
})
